/**
 * @file hospitals.js
 * @description This module provides functions to interact with the hospitals API.
 * It includes functions to add, retrieve, edit, and delete hospital records.
 *
 * @module hospitals
 */

import apiClient from "./apiClient";

/**
 * Adds a new hospital.
 * @async
 * @function addHospital
 * @param {Object} hospitalData - The data of the hospital to be added.
 * @returns {Promise<Object>} The added hospital data.
 */

export const addHospital = async (hospitalData) => {
  const response = await apiClient.post("/hospitals", hospitalData);
  return response.data;
};

/**
 * Retrieves all hospitals.
 * @async
 * @function getHospitals
 * @returns {Promise<Array>} A list of all hospitals.
 */

export const getHospitals = async ({ pageParam = 1, query = "", status ="" }) => {
  
  const response = await apiClient.get("/hospitals", {
    params: {
      page: pageParam,
      limit: 20,
      sortBy: "id:ASC",
      name: query || undefined,
      status: status || undefined,
    },
  });
  
  return response;
};

export const getHospitalsBySearch = async (query) => {
  const response = await apiClient.get(`/hospitals?name=${query}`);
  return response?.data?.data;
};

/**
 * Retrieves a specific hospital by ID.
 * @async
 * @function getHospital
 * @param {string} id - The ID of the hospital to retrieve.
 * @returns {Promise<Object>} The data of the retrieved hospital.
 */

export const getHospital = async (id) => {
  const response = await apiClient.get(`/hospitals/${id}`);
  return response.data;
};

/**
 * Edits an existing hospital.
 * @async
 * @function editHospital
 * @param {string} id - The ID of the hospital to edit.
 * @param {Object} editedHospital - The new data for the hospital.
 * @returns {Promise<Object>} The updated hospital data.
 */

export const editHospital = async ({ id, data }) => {
  const response = await apiClient.patch(`/hospitals/${id}`, data);
  return response.data;
};

/**
 * Deletes a hospital by ID.
 * @async
 * @function deleteHospital
 * @param {string} id - The ID of the hospital to delete.
 * @returns {Promise<Object>} The data of the deleted hospital.
 */

export const deleteHospital = async (id) => {
  const response = await apiClient.delete(`/hospitals/${id}`);
  return response.data;
};

export const getRequestedHospitalList = async (status) => {
  const response = await apiClient.get(`/hospitals?status=${status}`);
  return response.data?.data;
};

export const changeHospitalStatus = async ({ id, data }) => {
  const response = await apiClient.patch(
    `/hospitals/${id}/change-status`,
    data
  );
  return response.data;
};

export const getHospitalDocuments = async (id) => {
  const response = await apiClient.get(`/documents?hospital_id=${id}`);
  return response?.data?.data || [];
};
